import {KpisList, type AlternativeFuelRates, type PerformanceDescendantWithId} from '@domain/kpis'
import {HierarchyNodeMetadata} from '@domain/shared'
import {DateRange} from '@hconnect/uikit/src/common'
import {TableContainer} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {generateBarChartData} from '../components/actualVsPlanned.columns'
import {AcVsPlChartPie} from '../components/AcVsPlChartPie'
import {AcVsPlTable} from '../components/AcVsPlTable'
import {BarChart} from '../components/charts'
import {FEATURE_NAMESPACE} from '../consts'
import {AggregatedKpiData, BarChartData, Comment, CommentLocationData} from '../types'

type Props = {
  data: AggregatedKpiData
  hasQuarterlyData: boolean
  currentTab: KpisList
  hideCharts: boolean
  dateRange: DateRange<Date>
  setCommentLocationData: (id: CommentLocationData) => void
  comments: Comment[]
  hierarchy?: HierarchyNodeMetadata
  onLocationClick: (item: PerformanceDescendantWithId<AggregatedKpiData>) => void
}

export const AcVsPlContainer = ({
  data,
  hasQuarterlyData,
  currentTab,
  hideCharts = false,
  dateRange,
  setCommentLocationData,
  comments,
  hierarchy,
  onLocationClick
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)

  const barChartData: BarChartData = useMemo(
    () =>
      generateBarChartData({
        data,
        dateRange,
        currentTab,
        translationFn: (key: string, inputParams: {[key: string]: string} = {}) =>
          t(key, inputParams),
        locale: i18n.language
      }),
    [data, dateRange, i18n.language, t, currentTab]
  )

  return (
    <TableContainer component="div">
      {!hideCharts && currentTab !== KpisList.AlternativeFuelRate && (
        <BarChart data={barChartData} />
      )}

      {!hideCharts && currentTab === KpisList.AlternativeFuelRate && (
        // Note: this is shown only for AlternativeFuelRates and hence types are mapped
        <AcVsPlChartPie data={data as unknown as AlternativeFuelRates} dateRange={dateRange} />
      )}

      <AcVsPlTable
        data={data}
        hasQuarterlyData={hasQuarterlyData}
        currentTab={currentTab}
        dateRange={dateRange}
        setCommentLocationData={setCommentLocationData}
        comments={comments}
        hierarchy={hierarchy}
        onLocationClick={onLocationClick}
      />
    </TableContainer>
  )
}
